import { AssetBonusDisplay } from '@/components/AssetBonusDisplay';
import { GuideMenu } from '@/components/GuideMenu';

import { Link, createFileRoute } from '@tanstack/react-router';

const OverviewSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">
      Understanding Kingdom Relations
    </h3>
    <p className="text-[#b8b8b8] mb-2">
      For a Monarch, understanding and managing kingdom relations is a critical
      responsibility. While other players may not need to master diplomacy,
      having a basic understanding can be beneficial. Your kingdom&apos;s
      relationships with others influence everything from trade and alliances to
      conflict and war.
    </p>
    <p className="text-[#b8b8b8] mb-2">
      Managing these relations requires tact, strategy, and leadership. A poorly
      handled negotiation can escalate into war, while skillful diplomacy can
      forge powerful alliances. You can monitor your kingdom&apos;s current
      relations through the Kingdom Page.
    </p>
  </div>
);

const StancesSection = ({ stances }: { stances: any[] }) => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Stances</h3>
    <p className="text-[#b8b8b8] mb-2">
      Your kingdom&apos;s stance provides your kingdom with a unique set of
      circumstances. These circumstances are a reflection of your intentions and
      will give you bonuses to match. However, it also has its drawbacks.
      Normal stance changes create a 24-day lock before another ordinary
      change can be made. Rebuild and Jubilant have additional timing rules. All stance effects are suspended while your kingdom is at war.
    </p>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="text-left border-b border-white/10">
            <th className="p-2 text-[#f0d060]">Stance Name</th>
            <th className="p-2 text-[#f0d060]">Description</th>
            <th className="p-2 text-[#f0d060]">Bonuses</th>
          </tr>
        </thead>
        <tbody>
          {stances.map((stance) => (
            <tr key={stance.assetCode} className="border-b border-[rgba(212,175,55,0.12)]">
              <td className="p-2 text-yellow-300">{stance.name}</td>
              <td className="p-2 text-[#b8b8b8]">{stance.description}</td>
              <td className="p-2 text-[#b8b8b8]">
                <AssetBonusDisplay asset={stance} variant="guide" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <JubilantStanceNote />
  </div>
);

const JubilantStanceNote = () => (
  <div className="mt-4 bg-white/[0.03] p-4 rounded-lg border border-white/10">
    <h4 className="text-lg font-semibold text-[#f0d060] mb-2">
      Jubilant Stance
    </h4>
    <p className="text-[#b8b8b8] mb-2">
      The Jubilant Stance is automatically activated after a kingdom war
      concludes. It cannot be manually selected but serves as a post-war
      recovery period.
    </p>
    <ul className="text-[#b8b8b8] list-disc list-inside mb-2">
      <li>
        <strong>Duration:</strong> Jubilant Stance lasts 24-48 Mythean days.
      </li>
      <li>
        <strong>Cancellation:</strong> You may exit Jubilant Stance once the
        post-war stance-change lock expires by selecting a new stance. Otherwise,
        it will end automatically after 48 days, returning your kingdom to a
        normal stance.
      </li>
    </ul>
    <p className="text-[#b8b8b8] mb-2">
      <strong>Important Considerations During Jubilant Stance:</strong>
    </p>
    <ul className="text-[#b8b8b8] list-disc list-inside mb-2">
      <li>
        A post-war ceasefire is applied automatically with your last war
        opponent.
      </li>
      <li>
        You can leave Jubilant manually after the post-war stance-change lock
        expires or let it expire automatically at 48 days.
      </li>
      <li>
        War casualty returns can be claimed manually, with auto-claim behavior
        beginning in the later Jubilant window.
      </li>
      <li>
        Most recovery bonuses and penalties during Jubilant are scaled by war
        intensity.
      </li>
    </ul>
    <p className="text-[#b8b8b8]">
      For the full post-war timeline and claim flow, see{' '}
      <Link to="/jubilant" className="text-yellow-300 hover:text-yellow-200 underline">
        Jubilant Recovery
      </Link>
      .
    </p>
  </div>
);

const RelationsSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Relations</h3>
    <p className="text-[#b8b8b8] mb-2">
      Hostility between kingdoms develops as hostile actions accumulate,
      affecting diplomatic status. Initially, kingdoms have no relations, but
      escalating conflict increases hostility levels.
    </p>
    <h4 className="text-lg font-semibold text-[#f0d060] mb-2">
      Relation Levels & Escalation:
    </h4>
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="text-left border-b border-white/10">
            <th className="p-2 text-[#f0d060]">Relation Level</th>
            <th className="p-2 text-[#f0d060]">Description</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-[rgba(212,175,55,0.12)]">
            <td className="p-2 text-yellow-300">No Relations</td>
            <td className="p-2 text-[#b8b8b8]">
              Default state between kingdoms.
            </td>
          </tr>
          <tr className="border-b border-[rgba(212,175,55,0.12)]">
            <td className="p-2 text-yellow-300">Unfriendly</td>
            <td className="p-2 text-[#b8b8b8]">
              The first stage of rising hostility.
            </td>
          </tr>
          <tr className="border-b border-[rgba(212,175,55,0.12)]">
            <td className="p-2 text-yellow-300">Hostile</td>
            <td className="p-2 text-[#b8b8b8]">
              Requires monarch intervention to escalate from Unfriendly.
            </td>
          </tr>
          <tr className="border-b border-[rgba(212,175,55,0.12)]">
            <td className="p-2 text-yellow-300">War</td>
            <td className="p-2 text-[#b8b8b8]">
              The highest level of hostility—kingdoms engage in full-scale war.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p className="text-[#b8b8b8] mb-2">How Relations Progress:</p>
    <ul className="text-[#b8b8b8] list-disc list-inside mb-2">
      <li>Repeated hostile actions raise hostility levels.</li>
      <li>
        A Monarch or Co-Monarch must approve any escalation beyond Unfriendly.
      </li>
      <li>
        Ceasefires can be established, blocking all offensive actions between
        two kingdoms.
      </li>
      <li>
        A ceasefire requires mutual agreement between both Monarchs before
        taking effect.
      </li>
    </ul>
  </div>
);

const WarSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">War</h3>
    <p className="text-[#b8b8b8]">
      No kingdom can ever enter war with a kingdom less than half their networth
      or more than twice their networth. War is designed to allow two kingdoms
      to go head-to-head without fear of interference from other kingdoms.
      Winning wars will reward your kingdom with triumph points, a ranking
      system to see which kingdoms are best in war.
    </p>
    <div className="space-y-3">
      <p className="text-[#b8b8b8]">
        During war, a war overview is created in your war forums where the
        warring kingdoms may interact and view up to date statistics on the war.
      </p>
      <p className="text-[#b8b8b8]">
        During war, exploration is replacement-only. You may explore enough
        land to return to the size you were when the war began, but not beyond
        it. War exploration costs twice the usual gold and soldiers per acre.
      </p>
    </div>

    <div className="mt-6">
      <h4 className="text-lg font-semibold text-[#f0d060] mb-3">
        Ways to Enter War
      </h4>
      <div className="space-y-4">
        <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
          <h5 className="text-yellow-300 font-medium mb-2">
            1. Hostility Declaration
          </h5>
          <p className="text-sm text-[#b8b8b8]">
            A kingdom can declare war in times of extreme hostility when:
          </p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm text-[#b8b8b8] pl-4">
            <li>The other kingdom has 1000+ hostility points</li>
            <li>Your meter exceeds 599 points (unfriendly status)</li>
            <li>War begins instantly upon declaration</li>
          </ul>
        </div>

        <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
          <h5 className="text-yellow-300 font-medium mb-2">2. War Challenge</h5>
          <p className="text-sm text-[#b8b8b8]">
            If a kingdom is in networth range, a monarch can issue a War
            Challenge. The opponent must accept, and war starts exactly 48
            Mythea days after acceptance.
          </p>
        </div>

        <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
          <h5 className="text-yellow-300 font-medium mb-2">
            3. Resolution War
          </h5>
          <p className="text-sm text-[#b8b8b8]">
            Can be declared when all conditions are met:
          </p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm text-[#b8b8b8] pl-4">
            <li>Haven&apos;t beaten them in war (until after year 9)</li>
            <li>Target has more/equal war points (&gt;0)</li>
            <li>Target within 100%-110% networth range</li>
            <li>Your hostility meter ≤ their meter</li>
            <li>Equal or fewer provinces than target</li>
            <li>72 hours since your last war</li>
            <li>Target finished war 120+ hours ago</li>
          </ul>
          <p className="text-sm text-[#b8b8b8] mt-2">
            War starts in 36 + random 1-24 Mythean days. Target has 24 hours to
            refuse (rewards you 10% honor if refused).
          </p>
        </div>
      </div>
    </div>

    <div className="mt-6">
      <h4 className="text-lg font-semibold text-[#f0d060] mb-3">
        Ending a War
      </h4>
      <div className="space-y-4">
        <p className="text-[#b8b8b8]">
          War ends through surrender or when one kingdom has a clear advantage
          after sufficient time. Once minimum time is reached, both kingdoms can
          end the war through:
        </p>

        <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
          <h5 className="text-yellow-300 font-medium mb-2">Withdrawal</h5>
          <ul className="list-disc list-inside space-y-1 text-sm text-[#b8b8b8] pl-4">
            <li>No attacks allowed during two Mythea ticks prior</li>
            <li>
              Up to 5% of total province honor deducted and given to winner
            </li>
          </ul>
        </div>

        <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
          <h5 className="text-yellow-300 font-medium mb-2">Claim Win</h5>
          <p className="text-sm text-[#b8b8b8]">
            Available after 12 days if opponent has lost half of their:
          </p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm text-[#b8b8b8] pl-4">
            <li>Starting Acres</li>
            <li>Net Worth</li>
            <li>Honour</li>
          </ul>
          <p className="text-sm text-[#b8b8b8] mt-2">
            Results in 15% honor loss for losing kingdom. Also available if
            kingdom deletes or is suspended.
          </p>
        </div>

        <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
          <h5 className="text-yellow-300 font-medium mb-2">Capitulate</h5>
          <p className="text-sm text-[#b8b8b8]">
            Special early exit for a kingdom facing a pending forced war.
          </p>
          <ul className="list-disc list-inside space-y-1 mt-2 text-sm text-[#b8b8b8] pl-4">
            <li>Monarch action only</li>
            <li>Available during the first 23 Mythean ticks after the force is issued</li>
            <li>Cancels the pending forced war before the scheduled fight begins</li>
            <li>Credits the forcing kingdom with the win and transfers war points and honour</li>
            <li>Keeps the kingdom in its current stance and locks ordinary stance changes for 12 ticks</li>
            <li>Keeps both kingdoms protected by ceasefire for 48 ticks</li>
            <li>Reduces the forcing kingdom&apos;s force-war cooldown by seven days</li>
            <li>Does not grant Jubilant Stance, so normal War Chest cooldowns still apply</li>
          </ul>
        </div>

        <p className="text-[#b8b8b8]">
          After an ordinary withdrawal or claimed win, both kingdoms enter
          automatic post-war ceasefire and Jubilant Stance. Jubilant lasts
          24-48 Mythean days, during which hostile actions between the kingdoms
          are prevented. Capitulation follows the separate recovery path
          described above and does not itself grant Jubilant Stance.
        </p>
      </div>
    </div>
  </div>
);

const ForceWarSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">Force War</h3>
    <p className="text-[#b8b8b8]">
      Force War becomes available in Year 4. It allows kingdoms to force others
      into war under specific conditions.
    </p>

    <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
      <h4 className="text-lg font-semibold text-[#f0d060] mb-3">
        Requirements
      </h4>
      <ul className="list-disc list-inside space-y-2 text-sm text-[#b8b8b8] pl-4">
        <li>14-day cooldown between forces</li>
        <li>Costs entire forcing kingdom&apos;s gold resources</li>
        <li>Kingdoms must be within networth range</li>
        <li>Cannot have more provinces than target</li>
        <li>72 hours since your last war</li>
        <li>Target must be 120 hours out of their last war</li>
        <li>Cannot have more war points than target</li>
        <li>Hostile meter cannot exceed target&apos;s</li>
        <li>Target must have at least one war point</li>
        <li>Cannot force war with ceasefire kingdoms</li>
      </ul>
    </div>

    <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
      <h4 className="text-lg font-semibold text-[#f0d060] mb-3">
        Special Conditions
      </h4>
      <ul className="list-disc list-inside space-y-2 text-sm text-[#b8b8b8] pl-4">
        <li>
          War starts on random 48-74 hour timer aligned with forced
          kingdom&apos;s War Start Day
        </li>
        <li>
          If forced kingdom loses, provinces that lost honor get 30% returned
        </li>
        <li>
          Losing forced kingdoms receive +50% normal Jubilant Science bonus
        </li>
      </ul>
    </div>
  </div>
);

const WarPointsSection = () => (
  <div className="space-y-4">
    <h3 className="text-xl font-semibold text-[#e6c547]">War Points</h3>
    <p className="text-[#b8b8b8]">
      War points are allocated after war ends using the following formula:
    </p>

    <div className="bg-white/[0.03] p-4 rounded-lg border border-white/10">
      <h4 className="text-lg font-semibold text-[#f0d060] mb-3">
        Calculation Formula
      </h4>
      <p className="text-sm text-[#b8b8b8]">
        warpoints = ceil(loser war wins + 2 + floor((your provinces + their provinces) / 16) x war intensity)
      </p>

      <div className="mt-4">
        <h5 className="text-yellow-300 font-medium mb-2">Details</h5>
        <ul className="list-disc list-inside space-y-1 text-sm text-[#b8b8b8] pl-4">
          <li>The province term is continuous: floor(total provinces / 16) x intensity, not a fixed bracket.</li>
          <li>The win itself is worth 2 points, and the loser's war wins are added.</li>
          <li>Below 14 combined provinces the result is reduced by the shortfall, to a minimum of 1.</li>
          <li>All provinces count; protection is a province effect, not a status.</li>
          <li>Capitulation pays 1 point below 11 combined provinces or 2 at 11+, doubled from week 9.</li>
        </ul>
      </div>
    </div>
  </div>
);

export const Route = createFileRoute('/kingdom-relations/')({
  component: KingdomRelationsPage,
});

function KingdomRelationsPage() {
  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="max-w-6xl mx-auto space-y-8">
        <GuideMenu currentPath="/kingdom-relations" />

        {/* Main content section */}
        <section className="glass rounded-2xl p-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <img
              src="/images/nav/relations@2x.png"
              alt="Kingdom Relations"
              width={80}
              height={80}
              className="shrink-0"
            />
            <h2 className="text-2xl font-bold">Kingdom Relations</h2>
          </div>

          {/* Content sections */}
          <div className="space-y-12 text-[#b8b8b8] leading-relaxed">
            <OverviewSection />
            <StancesSection stances={[]} />
            <RelationsSection />
            <WarSection />
            <ForceWarSection />
            <WarPointsSection />
          </div>
        </section>
      </div>
    </div>
  );
}
